import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import CohortStudentPicker from '../../components/CohortStudentPicker';
import toast from 'react-hot-toast';
import { AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  GraduationCap,
  Layers,
  Plus,
  Sparkles,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { campusService, cohortService, courseService } from '../../api';
import ListToolbar from '../../components/list/ListToolbar';
import ListPagination from '../../components/list/ListPagination';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import { DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';
import AppSelect from '../../components/ui/AppSelect'

function parseList(res) {
  const data = res?.data ?? res;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data)) return data;
  return [];
}

const EMPTY_FORM = { name: '', description: '', campus: '', course: '' };

function Cohorts() {
  const [cohorts, setCohorts] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [enrollTarget, setEnrollTarget] = useState(null);
  const [rosterTarget, setRosterTarget] = useState(null);
  const [enrollCourseId, setEnrollCourseId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cohortsRes, campusesRes, coursesRes] = await Promise.all([
        cohortService.getCohorts({ page_size: 100 }),
        campusService.getCampuses(),
        courseService.getCourses({ page_size: 100 }),
      ]);
      setCohorts(parseList(cohortsRes));
      setCampuses(parseList(campusesRes).filter((c) => c.is_active !== false));
      setCourses(parseList(coursesRes));
    } catch {
      toast.error('Failed to load cohorts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const campusNameById = useMemo(() => {
    const map = {};
    campuses.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [campuses]);

  const filteredCohorts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return cohorts;
    return cohorts.filter((c) =>
      c.name?.toLowerCase().includes(q) ||
      c.description?.toLowerCase().includes(q) ||
      c.course_name?.toLowerCase().includes(q) ||
      campusNameById[c.campus]?.toLowerCase().includes(q)
    );
  }, [cohorts, searchTerm, campusNameById]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const pagedCohorts = useMemo(
    () => filteredCohorts.slice((page - 1) * pageSize, page * pageSize),
    [filteredCohorts, page, pageSize]
  );

  const coursesForCampus = useCallback(
    (campusId) => {
      if (!campusId) return courses;
      const id = Number(campusId);
      return courses.filter((c) => Number(c.campus) === id);
    },
    [courses]
  );

  const createFormCourses = useMemo(
    () => coursesForCampus(form.campus),
    [coursesForCampus, form.campus]
  );

  const enrollModalCourses = useMemo(
    () => (enrollTarget ? coursesForCampus(enrollTarget.campus) : []),
    [coursesForCampus, enrollTarget]
  );

  const stats = useMemo(() => {
    const totalStudents = cohorts.reduce((sum, c) => sum + (c.total_students_count ?? 0), 0);
    const linkedCourses = new Set(cohorts.filter((c) => c.course).map((c) => c.course)).size;
    return { total: cohorts.length, totalStudents, linkedCourses };
  }, [cohorts]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.campus) {
      toast.error('Name and campus are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await cohortService.createCohort({
        name: form.name.trim(),
        description: form.description.trim(),
        campus: Number(form.campus),
        course: form.course ? Number(form.course) : null,
      });
      toast.success('Cohort created — now add students to your roster.');
      setShowCreateModal(false);
      setForm(EMPTY_FORM);
      await fetchData();
      const created = res.data ?? res;
      if (created?.id) {
        setRosterTarget(created);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create cohort.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (cohort) => {
    if (!window.confirm(`Delete cohort "${cohort.name}"? This will not remove existing enrollments.`)) return;
    try {
      await cohortService.deleteCohort(cohort.id);
      toast.success('Cohort deleted.');
      fetchData();
    } catch {
      toast.error('Failed to delete cohort.');
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    const courseId = enrollCourseId || enrollTarget?.course;
    if (!enrollTarget || !courseId) {
      toast.error('Select a course to enroll the cohort.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await cohortService.enrollStudents(enrollTarget.id, Number(courseId));
      const data = res.data ?? res;
      toast.success(data.message || 'Students enrolled successfully.');
      setEnrollTarget(null);
      setEnrollCourseId('');
      await fetchData();
    } catch (err) {
      const errData = err?.response?.data;
      toast.error(
        errData?.error
        || errData?.detail
        || (typeof errData === 'string' ? errData : null)
        || 'Failed to enroll cohort.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <div className="text-xs font-bold uppercase tracking-widest text-secondary" style={{ letterSpacing: '0.12em' }}>
          Batch Operations
        </div>
        <div style={{ marginTop: 8, fontSize: '1.75rem', fontWeight: 900, lineHeight: 1.1 }}>
          <span className="text-primary">Manage Cohorts</span>
        </div>
      </div>

      <section className="dashboard-hero">
        <div className="dashboard-hero-content">
          <div className="dashboard-hero-kicker">
            <Sparkles size={12} style={{ display: 'inline', marginRight: 6, verticalAlign: '-1px' }} />
            AI-Powered Batch Enrollment
          </div>
          <h2>Group learners into cohorts and enroll entire classes in one action.</h2>
          <p>
            Organize students by intake, campus, or program — then bulk-enroll them into courses
            with intelligent defaults and consistent governance across your academy.
          </p>
        </div>
        <div className="dashboard-hero-visual" aria-hidden="true">
          <div className="ai-orb ai-orb-main" />
          <div className="ai-orb ai-orb-secondary" />
          <div className="ai-grid" />
        </div>
      </section>

      <div className="stat-grid" style={{ marginTop: 'var(--spacing-xl)' }}>
        <div className="stat-card stat-accent-cohorts">
          <div className="stat-icon-box md-icon-chip md-icon-chip-md md-icon-chip-primary">
            <Layers size={20} />
          </div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Active Cohorts</div>
        </div>
        <div className="stat-card stat-accent-approved">
          <div className="stat-icon-box md-icon-chip md-icon-chip-md md-icon-chip-success">
            <Users size={20} />
          </div>
          <div className="stat-value">{stats.totalStudents}</div>
          <div className="stat-label">Students in Cohorts</div>
        </div>
        <div className="stat-card stat-accent-completed">
          <div className="stat-icon-box md-icon-chip md-icon-chip-md md-icon-chip-secondary">
            <BookOpen size={20} />
          </div>
          <div className="stat-value">{stats.linkedCourses}</div>
          <div className="stat-label">Linked Courses</div>
        </div>
      </div>

      <ListToolbar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search cohorts by name, campus, or course…"
      >
        <button type="button" className="btn btn-primary hover-lift" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} style={{ marginRight: 8 }} />
          New Cohort
        </button>
      </ListToolbar>

      {loading ? (
        <div className="text-secondary" style={{ textAlign: 'center', padding: 'var(--spacing-xl)' }}>
          Loading cohorts...
        </div>
      ) : filteredCohorts.length === 0 ? (
        <div className="cohort-empty-state">
          <div className="cohort-empty-icon">
            <GraduationCap size={36} />
          </div>
          <h3 className="text-body" style={{ marginBottom: 8, fontSize: '1.2rem' }}>No cohorts yet</h3>
          <p className="text-secondary" style={{ marginBottom: 'var(--spacing-lg)', maxWidth: 420, marginInline: 'auto' }}>
            Create a cohort to group students for batch enrollment into courses across your campus.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            Create your first cohort
          </button>
        </div>
      ) : (
        <>
          <DataRowList>
            {pagedCohorts.map((cohort, index) => (
              <DataRow
                key={cohort.id}
                index={index}
                leading={(
                  <div className="ux-row-thumb">
                    <Layers size={20} />
                  </div>
                )}
                title={cohort.name}
                subtitle={cohort.description?.trim() || 'No description'}
                meta={(
                  <>
                    <span>{campusNameById[cohort.campus] || `Campus #${cohort.campus}`}</span>
                    <span>{cohort.total_students_count ?? 0} students</span>
                    <span>{cohort.course_name || 'No default course'}</span>
                  </>
                )}
                actions={(
                  <>
                    <button type="button" className="ux-btn-sm ux-btn-ghost" onClick={() => setRosterTarget(cohort)}>
                      <Users size={14} /> Roster
                    </button>
                    <button
                      type="button"
                      className="ux-btn-sm ux-btn-primary"
                      onClick={() => {
                        const campusCourses = coursesForCampus(cohort.campus);
                        const defaultCourse =
                          cohort.course && campusCourses.some((c) => c.id === cohort.course)
                            ? String(cohort.course)
                            : '';
                        setEnrollTarget(cohort);
                        setEnrollCourseId(defaultCourse);
                      }}
                    >
                      <UserPlus size={14} /> Enroll
                    </button>
                    <button type="button" className="ux-icon-btn danger" onClick={() => handleDelete(cohort)} aria-label={`Delete ${cohort.name}`}>
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              />
            ))}
          </DataRowList>
          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={filteredCohorts.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </>
      )}

      <AnimatePresence>
        {rosterTarget && (
          <CohortStudentPicker
            key={rosterTarget.id}
            cohort={rosterTarget}
            onClose={() => setRosterTarget(null)}
            onSaved={fetchData}
          />
        )}
      </AnimatePresence>

      {showCreateModal && (
        <div className="cohort-modal-backdrop" onClick={() => !submitting && setShowCreateModal(false)} role="presentation">
          <div className="cohort-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="create-cohort-title">
            <h3 id="create-cohort-title">Create Cohort</h3>
            <form onSubmit={handleCreate}>
              <div className="cohort-form-field">
                <label htmlFor="cohort-name">Cohort name</label>
                <input
                  id="cohort-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Class of 2026 — Data Science"
                  required
                />
              </div>
              <div className="cohort-form-field">
                <label htmlFor="cohort-desc">Description</label>
                <textarea
                  id="cohort-desc"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Optional notes about this intake group..."
                />
              </div>
              <div className="cohort-form-field">
                <label htmlFor="cohort-campus">Campus</label>
                <AppSelect
                  id="cohort-campus"
                  value={form.campus}
                  onChange={(e) => setForm((f) => ({ ...f, campus: e.target.value, course: '' }))}
                  required
                >
                  <option value="">Select campus</option>
                  {campuses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </AppSelect>
              </div>
              <div className="cohort-form-field">
                <label htmlFor="cohort-course">Default course (optional)</label>
                <AppSelect
                  id="cohort-course"
                  value={form.course}
                  onChange={(e) => setForm((f) => ({ ...f, course: e.target.value }))}
                >
                  <option value="">None — assign at enroll time</option>
                  {createFormCourses.map((c) => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </AppSelect>
              </div>
              <div className="cohort-modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowCreateModal(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Cohort'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {enrollTarget && (
        <div className="cohort-modal-backdrop" onClick={() => !submitting && setEnrollTarget(null)} role="presentation">
          <div className="cohort-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="enroll-cohort-title">
            <h3 id="enroll-cohort-title">Bulk Enroll — {enrollTarget.name}</h3>
            <p className="text-secondary" style={{ marginBottom: 'var(--spacing-md)', fontSize: '0.95rem' }}>
              Enroll all {enrollTarget.total_students_count ?? 0} students in this cohort into a course.
              Free courses are approved immediately; paid courses require payment before access.
              Existing enrollments for the same cohort and course are skipped.
            </p>
            <form onSubmit={handleEnroll}>
              <div className="cohort-form-field">
                <label htmlFor="enroll-course">Target course</label>
                <AppSelect
                  id="enroll-course"
                  value={enrollCourseId}
                  onChange={(e) => setEnrollCourseId(e.target.value)}
                  required
                >
                  <option value="">Select course</option>
                  {enrollModalCourses.map((c) => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </AppSelect>
              </div>
              <div className="cohort-modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setEnrollTarget(null)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Enrolling...' : 'Enroll Cohort'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default Cohorts;
